import React from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { Edit, Trash2, Mail, Phone, MapPin, Link as LinkIcon, Building } from 'lucide-react';
import { Contact } from '../../services/types';

interface ContactsListViewProps {
    contacts: Contact[];
    canDelete: boolean;
    onEdit: (contact: Contact) => void;
    onDelete: (contact: Contact) => void;
}

const ContactsListView: React.FC<ContactsListViewProps> = ({
    contacts,
    canDelete,
    onEdit,
    onDelete,
}) => {
    const getTypeColor = (type?: string | null) => {
        switch (type?.toLowerCase()) {
            case 'supplier': return 'primary';
            case 'client': return 'success';
            case 'partner': return 'info';
            case 'vendor': return 'warning';
            default: return 'secondary';
        }
    };

    const getWhatsAppLink = (phoneNumber: string) => {
        // Remove all non-numeric characters except +
        const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
        return `https://wa.me/${cleanNumber}`;
    };

    return (
        <div className="row g-3">
            {contacts.map((contact) => (
                <div key={contact.public_id} className="col-12 col-md-6 col-lg-4">
                    <Card className="h-100 border-0 shadow-sm" style={{ transition: 'transform 0.2s, box-shadow 0.2s' }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                        }}
                    >
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="flex-grow-1">
                                    <h5 className="mb-1" style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827' }}>
                                        {contact.name}
                                    </h5>
                                    {contact.company_name && (
                                        <div className="text-muted small d-flex align-items-center gap-1 mb-2">
                                            <Building size={14} />
                                            {contact.company_name}
                                        </div>
                                    )}
                                    {contact.type && (
                                        <Badge bg={getTypeColor(contact.type)} className="mt-1">
                                            {contact.type}
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            <div className="mb-3" style={{ minHeight: '80px' }}>
                                {contact.email && (
                                    <div className="mb-2 small">
                                        <Mail size={14} className="me-2 text-muted" />
                                        <a href={`mailto:${contact.email}`} style={{ textDecoration: 'none', color: '#4b5563' }}>
                                            {contact.email}
                                        </a>
                                    </div>
                                )}
                                {contact.number && (
                                    <div className="mb-2 small">
                                        <Phone size={14} className="me-2 text-muted" />
                                        <a href={getWhatsAppLink(contact.number)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#4b5563' }}>
                                            {contact.number}
                                        </a>
                                    </div>
                                )}
                                {contact.location && (
                                    <div className="mb-2 small">
                                        <MapPin size={14} className="me-2 text-muted" />
                                        <span style={{ color: '#6b7280' }}>{contact.location}</span>
                                    </div>
                                )}
                                {contact.link && (
                                    <div className="mb-2 small">
                                        <LinkIcon size={14} className="me-2 text-muted" />
                                        <a href={contact.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#4b5563' }}>
                                            Website
                                        </a>
                                    </div>
                                )}
                            </div>

                            {contact.observation && (
                                <div className="mb-3">
                                    <p className="small text-muted mb-0" style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                    }}>
                                        {contact.observation}
                                    </p>
                                </div>
                            )}

                            {canDelete && (
                                <div className="d-flex gap-2 mt-3 pt-3 border-top">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="flex-grow-1"
                                        onClick={() => onEdit(contact)}
                                    >
                                        <Edit size={14} className="me-1" />
                                        Edit
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="flex-grow-1"
                                        onClick={() => onDelete(contact)}
                                    >
                                        <Trash2 size={14} className="me-1" />
                                        Delete
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            ))}
        </div>
    );
};

export default ContactsListView;
